import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { ArrowDownRight, Zap, Check } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { api } from "@/lib/api";

const TICKER = [
  "SPURR / USD",
  "1,000,000,000 SUPPLY",
  "SOLANA SPL NATIVE",
  "$19 TO PUBLISH",
  "AUDITED · LP LOCKED 24MO",
  "MICA COMPLIANT",
  "HOWEY-TESTED UTILITY",
];

function LiveCounter() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/stats/public").then((r) => {
      if (!cancelled && r.data && typeof r.data === "object") setStats(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!stats) return null;
  const coinsTotal = Number(stats.coins_total) || 0;
  return (
    <div
      className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border border-[#3a3a3a] bg-[#121212]/85 backdrop-blur px-5 py-3 font-mono text-xs"
      data-testid="hero-live-counter"
    >
      <span className="flex items-center gap-2 text-[#e2e2e2]">
        <span className="w-1.5 h-1.5 bg-[#00ff41] rounded-full animate-pulse" />
        <span className="text-white font-bold">{coinsTotal.toLocaleString()}</span>
        <span className="text-[#d0d0d0]">coins launched</span>
      </span>
      <span className="flex items-center gap-2 text-[#e2e2e2]">
        <span className="text-[#cfff04] font-bold">{(Number(stats.tips_sol) || 0).toFixed(2)}</span>
        <span className="text-[#d0d0d0]">SOL tipped to creators</span>
      </span>
      {stats.coins_this_week > 0 && (
        <span className="flex items-center gap-2 text-[#e2e2e2]">
          <span className="text-white font-bold">+{stats.coins_this_week}</span>
          <span className="text-[#d0d0d0]">this week</span>
        </span>
      )}
    </div>
  );
}

export default function Hero() {
  const { anyConnected, primaryAddress, short } = useWallet();

  return (
    <section id="top" className="relative pt-16 overflow-hidden">
      {/* background */}
      <div
        className="absolute inset-0 z-0 opacity-40"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/3681aa5c-86db-4b26-b358-26b9f5039b01/images/61a5bf1b7c557feb32d3446f6e9a481e3467c4b3e91856afa61c7a0c90b1f78d.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-[#050505]/70 z-[1]" />
      <div className="absolute inset-0 grid-bg opacity-50 z-[2]" />
      <div className="absolute inset-0 grain z-[3]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-16 md:pt-32 md:pb-24">
        {/* overline */}
        <div className="flex items-center gap-3 mb-6" data-testid="hero-status">
          <span className="w-2 h-2 bg-[#00ff41] live-dot" />
          <span className="font-mono text-[11px] tracking-[0.4em] text-[#e2e2e2] uppercase">
            Live on Solana mainnet · AI-assisted launches
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="font-display font-black text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] tracking-[-0.04em]"
            >
              Launch your
              <br />
              own coin.
              <br />
              <span className="text-[#cfff04]">For pocket change.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#e2e2e2] leading-relaxed">
              <span className="font-mono text-white">SolSpur</span> is a launchpad for real, tradable
              Solana tokens — <span className="text-[#cfff04] font-mono">$19 to publish</span>, AI-generated
              logo + whitepaper, live market data, ready in 60 seconds. Our genesis token{" "}
              <a href="/c/spurr" className="text-[#cfff04] font-mono underline hover:no-underline">
                $SPURR
              </a>{" "}
              powers the platform — a utility token with <span className="text-[#cfff04] font-mono">planned</span> perks like fee discounts, a community airdrop &amp; governance. Utility, not an investment.
            </p>

            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <Link
                to="/create"
                data-testid="cta-launch-now"
                className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-7 py-4 flex items-center gap-2"
              >
                <Zap size={16} /> Launch your coin — no wallet needed
              </Link>
              <Link
                to="/c/spurr"
                data-testid="cta-see-example"
                className="btn-ghost border border-[#555] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 flex items-center gap-2"
              >
                See a live coin page <ArrowDownRight size={16} />
              </Link>
              {anyConnected && (
                <span className="self-center font-mono text-xs text-[#d0d0d0] flex items-center gap-1" data-testid="hero-wallet-connected">
                  <Check size={12} className="text-[#00ff41]" /> {short(primaryAddress)} connected
                </span>
              )}
            </div>

            <LiveCounter />

            {/* hero stats */}
            <dl className="mt-14 grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#3a3a3a]">
              {[
                ["$19", "To publish, once"],
                ["~60s", "To a live coin page"],
                ["7", "Public trust checks"],
                ["SPL", "Real Solana token"],
              ].map(([v, l]) => (
                <div key={l} className="border-r border-b border-[#3a3a3a] p-4 md:p-6">
                  <dt className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#d0d0d0]">{l}</dt>
                  <dd className="font-display text-2xl md:text-3xl font-bold mt-2">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* right rail: terminal card */}
          <aside className="col-span-12 lg:col-span-4">
            <div
              className="border border-[#3a3a3a] bg-[#121212] p-5 font-mono text-xs leading-relaxed"
              data-testid="hero-terminal"
            >
              <div className="flex items-center justify-between border-b border-[#3a3a3a] pb-2 mb-3">
                <span className="text-[#e2e2e2]">~/how-a-launch-looks.sh</span>
                <span className="text-[#d0d0d0]">example</span>
              </div>
              <pre className="whitespace-pre-wrap text-[#e2e2e2]">
{`> solspur create "Cosmic Otter" --ticker OTTER
✓ AI name, logo & whitepaper ready   (42s)
> solspur publish
✓ Live at solspur.com/c/otter + share card
> solspur mint --revoke mint,freeze   (Phantom)
✓ Real SPL token · nobody can print more
> solspur pool --raydium 0.5 SOL      (Phantom)
✓ Tradable · trust 100/100 · Verified
`}
              </pre>
              <div className="mt-3 pt-3 border-t border-[#3a3a3a] text-[#cfff04]">
                You pay: <span className="text-white">$19 once</span> <span className="text-[#d0d0d0]">+ Solana network fees when you mint</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ticker */}
      <div
        className="relative z-10 border-y border-[#3a3a3a] bg-[#121212] py-3"
        data-testid="hero-ticker"
      >
        <Marquee gradient={false} speed={48} pauseOnHover>
          {TICKER.concat(TICKER).map((t, i) => (
            <span
              key={`${t}-${i}`}
              className="font-mono text-xs uppercase tracking-[0.3em] text-[#cfff04] mx-8"
            >
              {t} <span className="text-[#d0d0d0] mx-4">//</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
